'use client';

import { cn } from '@heidi/utils';
import { AnimatePresence } from 'motion/react';
import * as m from 'motion/react-m';
import { useLayoutEffect, useRef } from 'react';

import { useLoaderPhase } from './hooks/useLoaderPhase';
import { SkeletonSections } from './skeletonSections';
import { StatusText } from './statusText';
import { UnderstandingPhase } from './understandingPhase';

interface TemplateAssemblyLoaderProps {
  className?: string;
}

export function TemplateAssemblyLoader({ className }: TemplateAssemblyLoaderProps) {
  const { phase, userInput, keywords } = useLoaderPhase();

  // Track if sections have been shown before (for entrance animation)
  const hasShownSections = useRef(false);
  const showSections =
    phase === 'building' || phase === 'drafting' || phase === 'complete';

  // Determine if this is the first time showing sections
  // IMPORTANT: Do NOT mutate ref during render - causes race conditions with Strict Mode
  const isEntering = showSections && !hasShownSections.current;

  // Update the ref AFTER render but BEFORE paint using useLayoutEffect
  // This ensures the entrance animation plays on first mount, then ref is set
  useLayoutEffect(() => {
    if (showSections && !hasShownSections.current) {
      hasShownSections.current = true;
    }
  }, [showSections]);

  // Determine the phase to pass to SkeletonSections
  const skeletonPhase =
    phase === 'complete' ? 'complete' : phase === 'building' ? 'building' : 'drafting';

  return (
    <m.div
      className={cn(
        'flex size-full flex-col items-center justify-center gap-4',
        className
      )}
      // Exit animation: fade out with subtle scale after completion flash
      exit={{
        opacity: 0,
        scale: 0.98,
        transition: {
          duration: 0.4,
          ease: [0.23, 1, 0.32, 1], // ease-out-quint
          delay: 0.4, // Wait for completion flash (lines fill to 100%)
        },
      }}
    >
      {/* Main animation container matching Figma design */}
      <div
        className={cn(
          'relative h-[200px] w-[320px] overflow-hidden rounded-xl',
          'border-[0.5px] border-border bg-surface'
        )}
      >
        {/* Understanding phase with exit animation */}
        <AnimatePresence>
          {phase === 'understanding' && (
            <UnderstandingPhase key="understanding" keywords={keywords} />
          )}
        </AnimatePresence>

        {/* Skeleton sections persist across building/drafting/complete - no AnimatePresence swap */}
        {showSections && (
          <SkeletonSections phase={skeletonPhase} isEntering={isEntering} />
        )}
      </div>

      {/* Status text below container */}
      <StatusText phase={phase} context={userInput} />
    </m.div>
  );
}
